import React from 'react';
import { Building, Calculator, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import AdSense from '../components/AdSense';
import { useSEO } from '../hooks/useSEO';

const RealEstate: React.FC = () => {
  useSEO({
    title: 'Financiamento Imobiliário 2026: Tabelas SAC vs Price e Amortização | O Teu Dinheiro',
    description: 'Entenda como funciona o financiamento imobiliário em 2026, diferenças entre Tabela SAC e Price, uso do FGTS e como quitar 30 anos em 10.',
    keywords: 'financiamento imobiliário 2026, tabela SAC vs Price, amortização financiamento, usar FGTS imóvel, juros financiamento caixa',
    url: 'https://www.oteudinheiro.com.br/financiamento'
  });
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-20">
        <div className="container-custom text-center">
          <Building className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Financiamento Imobiliário
          </h1>
          <p className="text-xl md:text-2xl text-teal-100 max-w-3xl mx-auto">
            Como financiar seu imóvel de forma inteligente e sustentável
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left Column */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                O que é financiamento imobiliário?
              </h2>
              
              <div className="prose prose-lg text-gray-600 mb-8">
                <p className="mb-4">
                  O financiamento imobiliário é um empréstimo de longo prazo para 
                  comprar um imóvel, com o próprio imóvel servindo como garantia.
                </p>
                <p className="mb-4">
                  É uma forma de realizar o sonho da casa própria sem precisar 
                  juntar todo o dinheiro de uma vez.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-6">
                Tipos de financiamento
              </h3>
              
              <div className="space-y-6 mb-8">
                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-teal-500">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                    <Building className="w-5 h-5 text-teal-600 mr-2" />
                    Sistema SAC
                  </h4>
                  <p className="text-gray-600 mb-3">
                    Parcelas decrescentes: começa alta e vai diminuindo ao longo do tempo.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>• Parcelas decrescentes</li>
                    <li>• Juros menores no total</li>
                    <li>• Ideal para quem quer pagar menos juros</li>
                  </ul>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                  <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                    <Calendar className="w-5 h-5 text-blue-600 mr-2" />
                    Sistema Price
                  </h4>
                  <p className="text-gray-600 mb-3">
                    Parcelas fixas: o valor da parcela permanece o mesmo durante todo o financiamento.
                  </p>
                  <ul className="text-sm text-gray-500 space-y-1">
                    <li>• Parcelas fixas</li>
                    <li>• Facilita o planejamento</li>
                    <li>• Juros maiores no total</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Requisitos básicos
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <div className="bg-teal-100 p-2 rounded-lg">
                      <DollarSign className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Entrada mínima</h4>
                      <p className="text-gray-600 text-sm">Geralmente 20% do valor do imóvel</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Renda comprovada</h4>
                      <p className="text-gray-600 text-sm">Capacidade de pagar as parcelas</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Calculator className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Comprometimento</h4>
                      <p className="text-gray-600 text-sm">Parcela não pode exceder 30% da renda</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Section */}
      <section className="py-8">
        <div className="container-custom px-4">
          <AdSense />
        </div>
      </section>

      {/* Calculator Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Simule seu financiamento
          </h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-50 rounded-xl p-8">
              <p className="text-center text-gray-600 mb-8">
                Use nossa calculadora para simular diferentes cenários de financiamento 
                e encontrar a melhor opção para você.
              </p>
              
              <div className="text-center">
                <a
                  href="/calculadoras"
                  className="btn bg-teal-600 text-white hover:bg-teal-700"
                >
                  Acessar Calculadora
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tips Section */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Dicas para um bom financiamento
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Economize para a entrada
              </h3>
              <p className="text-secondary-700">
                Quanto maior a entrada, menor será o valor financiado e, 
                consequentemente, os juros pagos.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Compare as taxas
              </h3>
              <p className="text-secondary-700">
                Pesquise em diferentes bancos e compare as taxas de juros. 
                Pequenas diferenças fazem grande diferença no longo prazo.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Escolha o prazo adequado
              </h3>
              <p className="text-secondary-700">
                Prazos mais longos têm parcelas menores, mas juros maiores. 
                Encontre o equilíbrio ideal para sua situação.
              </p>
            </div>

            <div className="tip-box">
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                Considere amortizações
              </h3>
              <p className="text-secondary-700">
                Amortizar o principal reduz o tempo de financiamento e 
                os juros pagos. Use o FGTS se tiver direito.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-teal-600 text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">
            Realize o sonho da casa própria!
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Com planejamento e conhecimento, o financiamento pode ser uma excelente opção.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/calculadoras"
              className="btn bg-white text-teal-600 hover:bg-gray-100"
            >
              Simular Financiamento
            </a>
            <a
              href="/aluguel-vs-compra"
              className="btn bg-transparent text-white border-2 border-white hover:bg-white hover:text-teal-600"
            >
              Aluguel vs Compra
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstate;
